import { useState } from "react";
import { 
  ScatterChart, 
  Scatter, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Tooltip,
  ZAxis
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { type Feedback } from "@/types";

type PrioritizationChartProps = {
  feedback: Feedback[];
  scoreMethod: 'value_effort' | 'rice';
};

const PrioritizationChart = ({ feedback, scoreMethod }: PrioritizationChartProps) => {
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  // Format data for the chart based on scoring method
  const getChartData = () => {
    return feedback
      .filter(item => {
        if (scoreMethod === 'value_effort') {
          return item.value_score !== null && item.effort_score !== null;
        } else {
          // RICE score = Reach * Impact * Confidence / Effort
          return item.reach_score !== null && 
                item.impact_score !== null && 
                item.confidence_score !== null && 
                item.effort_rice_score !== null && 
                item.effort_rice_score > 0;
        }
      })
      .map(item => {
        let x, y, score;
        
        if (scoreMethod === 'value_effort') {
          // For value/effort: x = value, y = 1/effort (higher is better)
          x = item.value_score;
          y = item.effort_score ? 10 - item.effort_score : 0; // Invert effort so higher is better
          score = item.value_score && item.effort_score 
            ? (item.value_score / (item.effort_score || 1)) 
            : 0;
        } else {
          // For RICE: x = Reach * Impact, y = Confidence / Effort
          x = (item.reach_score || 0) * (item.impact_score || 0);
          y = (item.confidence_score || 0) / (item.effort_rice_score || 1);
          score = item.priority_score || 0;
        }
        
        return {
          id: item.id,
          x,
          y,
          score,
          title: item.title,
          item
        };
      });
  };

  const chartData = getChartData();

  // Calculate dynamic domains based on data
  const calculateDomain = (axis: 'x' | 'y') => {
    if (chartData.length === 0) {
      return scoreMethod === 'value_effort' ? [0, 10] : [0, 10];
    }
    
    const values = chartData.map(item => item[axis]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    if (scoreMethod === 'value_effort') {
      // For value/effort, keep fixed domain of 0-10
      return [0, 10];
    } else {
      // For RICE, calculate a nice round domain based on data
      const padded = Math.ceil(max * 1.1); // Add 10% padding
      return [0, padded];
    }
  };

  const xDomain = calculateDomain('x');
  const yDomain = calculateDomain('y');

  const getQuadrantLabel = (x: number, y: number) => {
    if (scoreMethod === 'value_effort') {
      if (x >= 5 && y >= 5) return "High Value, Low Effort (Do)";
      if (x >= 5 && y < 5) return "High Value, High Effort (Plan)";
      if (x < 5 && y >= 5) return "Low Value, Low Effort (Consider)";
      return "Low Value, High Effort (Avoid)";
    } else {
      const xMid = xDomain[1] / 2;
      const yMid = yDomain[1] / 2;
      
      if (x >= xMid && y >= yMid) return "High Impact, High Confidence (Do)";
      if (x >= xMid && y < yMid) return "High Impact, Low Confidence (Research)";
      if (x < xMid && y >= yMid) return "Low Impact, High Confidence (Consider)";
      return "Low Impact, Low Confidence (Avoid)";
    }
  };

  const handlePointClick = (data: any) => {
    setSelectedFeedback(data.item);
  };

  // Calculate mid points for quadrant lines 
  const xMid = scoreMethod === 'value_effort' ? 5 : xDomain[1] / 2;
  const yMid = scoreMethod === 'value_effort' ? 5 : yDomain[1] / 2;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          Feedback Prioritization - {scoreMethod === 'value_effort' ? 'Value/Effort' : 'RICE Score'} Matrix
        </CardTitle>
        <CardDescription>
          Click on a point to view feedback details. Points are sized by overall priority score.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[500px] w-full">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name={scoreMethod === 'value_effort' ? 'Value' : 'Reach × Impact'} 
                  domain={xDomain}
                  label={{ 
                    value: scoreMethod === 'value_effort' ? 'Value' : 'Reach × Impact', 
                    position: 'bottom' 
                  }}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name={scoreMethod === 'value_effort' ? 'Ease (10 - Effort)' : 'Confidence / Effort'} 
                  domain={yDomain}
                  label={{ 
                    value: scoreMethod === 'value_effort' ? 'Ease (10 - Effort)' : 'Confidence / Effort', 
                    angle: -90, 
                    position: 'left' 
                  }}
                />
                <ZAxis 
                  type="number" 
                  dataKey="score" 
                  range={[50, 200]} 
                  name="Priority Score" 
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const quadrant = getQuadrantLabel(data.x, data.y);
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="font-semibold">{data.title}</div>
                          <div className="text-sm text-muted-foreground">{quadrant}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Score: {data.score.toFixed(2)}
                          </div>
                          <div className="text-xs mt-1">Click for details</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter 
                  name="Feedback" 
                  data={chartData}
                  fill="hsl(var(--primary))"
                  onClick={handlePointClick}
                  cursor="pointer"
                />

                {/* Quadrant dividing lines - now dynamic based on data */}
                <line 
                  x1={xMid} 
                  y1={0} 
                  x2={xMid} 
                  y2={yDomain[1]} 
                  stroke="#aaa" 
                  strokeDasharray="3 3" 
                />
                <line 
                  x1={0} 
                  y1={yMid} 
                  x2={xDomain[1]} 
                  y2={yMid} 
                  stroke="#aaa" 
                  strokeDasharray="3 3" 
                />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">
                No scored feedback items available. Score some feedback items to see them here.
              </p>
            </div>
          )}
        </div>

        {/* Feedback detail dialog */}
        <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedFeedback?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-muted-foreground">{selectedFeedback?.description}</p>
              <div className="grid grid-cols-2 gap-4">
                {scoreMethod === 'value_effort' ? (
                  <>
                    <div>
                      <span className="text-sm font-medium">Value:</span>
                      <span className="ml-2">{selectedFeedback?.value_score}/10</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium">Effort:</span>
                      <span className="ml-2">{selectedFeedback?.effort_score}/10</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-sm font-medium">Reach:</span>
                      <span className="ml-2">{selectedFeedback?.reach_score}/10</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium">Impact:</span>
                      <span className="ml-2">{selectedFeedback?.impact_score}/10</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium">Confidence:</span>
                      <span className="ml-2">{selectedFeedback?.confidence_score}/10</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium">Effort:</span>
                      <span className="ml-2">{selectedFeedback?.effort_rice_score}/10</span>
                    </div>
                  </>
                )}
                <div className="col-span-2">
                  <span className="text-sm font-medium">Priority Score:</span>
                  <span className="ml-2">
                    {scoreMethod === 'value_effort' 
                      ? ((selectedFeedback?.value_score || 0) / (selectedFeedback?.effort_score || 1)).toFixed(2)
                      : selectedFeedback?.priority_score?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PrioritizationChart;
